import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Platform, Animated, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { useNavigation } from 'expo-router';
import Icon from '@/components/Icon';
import ExploreCardOverlay from './ExploreCardOverlay';
import { exploreMediaZoomStyles as mz } from './exploreCardMediaZoomStyles';
import { pickMusicGradient } from '@/constants/Colors';

const isWeb = Platform.OS === 'web';

const NUM_BARS = 12;

// Only one explore-card preview may play at a time. Hover used to start
// playback asynchronously; racing createAsync calls left multiple songs
// audible with no way to stop them short of a reload.
let activeStopPlayback = null;

function claimActivePlayback(stopFn) {
  if (activeStopPlayback && activeStopPlayback !== stopFn) {
    try { activeStopPlayback(); } catch (_) {}
  }
  activeStopPlayback = stopFn;
}

function releaseActivePlayback(stopFn) {
  if (activeStopPlayback === stopFn) {
    activeStopPlayback = null;
  }
}

function hashFloat(id, index, seed = 0) {
  let h = seed;
  const s = `${id}-${index}`;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return (Math.abs(h) % 1000) / 1000;
}

// Animated waveform bar — web only. Height can't be native-driven so this
// animation runs on the JS thread. With 12 bars per card × every visible music
// card in the Explore feed, running this on iOS/Android saturates the JS
// thread and freezes the feed (the idle loop runs even when the user isn't
// hovering, which doesn't exist on touch anyway). Native cards render the
// static variant below.
const AnimatedWaveformBar = React.memo(({ index, itemId, active }) => {
  const baseHeight = 6 + hashFloat(itemId, index, 1) * 10;
  const anim = useRef(new Animated.Value(baseHeight)).current;
  const loopRef = useRef(null);

  useEffect(() => {
    loopRef.current?.stop();

    if (active) {
      const minH = 4;
      const maxH = 28;
      const duration = 300 + hashFloat(itemId, index, 2) * 400;
      loopRef.current = Animated.loop(
        Animated.sequence([
          Animated.timing(anim, { toValue: minH + hashFloat(itemId, index, 3) * (maxH - minH), duration, useNativeDriver: false }),
          Animated.timing(anim, { toValue: minH + hashFloat(itemId, index, 4) * (maxH - minH) * 0.4, duration: duration * 0.8, useNativeDriver: false }),
        ]),
      );
      loopRef.current.start();
    } else {
      // Idle: each bar breathes at its own pace with a random phase delay
      const idleMin = Math.max(3, baseHeight - 3);
      const idleMax = baseHeight + 9;
      const duration = 700 + hashFloat(itemId, index, 6) * 700;
      const phaseDelay = hashFloat(itemId, index, 7) * 900;

      const startLoop = () => {
        loopRef.current = Animated.loop(
          Animated.sequence([
            Animated.timing(anim, { toValue: idleMax, duration, useNativeDriver: false }),
            Animated.timing(anim, { toValue: idleMin, duration: duration * 0.85, useNativeDriver: false }),
          ]),
        );
        loopRef.current.start();
      };

      // Stagger start so bars don't all move in unison
      const t = setTimeout(startLoop, phaseDelay);
      return () => {
        clearTimeout(t);
        loopRef.current?.stop();
      };
    }

    return () => loopRef.current?.stop();
  }, [active, anim, baseHeight, index, itemId]);

  return (
    <Animated.View
      style={[
        styles.waveBar,
        { height: anim, opacity: active ? 0.9 : 0.5 + hashFloat(itemId, index, 5) * 0.35 },
      ]}
    />
  );
});

// Static waveform bar for native — same visual footprint as the animated
// version at rest, just no JS-thread animation cost. Native never fires
// hover events anyway, so users weren't seeing any motion here either.
const StaticWaveformBar = React.memo(({ index, itemId }) => {
  const baseHeight = 6 + hashFloat(itemId, index, 1) * 10;
  const opacity = 0.5 + hashFloat(itemId, index, 5) * 0.35;
  return <View style={[styles.waveBar, { height: baseHeight, opacity }]} />;
});

AnimatedWaveformBar.displayName = 'AnimatedWaveformBar';
StaticWaveformBar.displayName = 'StaticWaveformBar';

const WaveformBar = isWeb ? AnimatedWaveformBar : StaticWaveformBar;

const ExploreCardMusic = React.memo(({ item, style, hovered }) => {
  // `item.gradient` is stamped by useExploreStore when items are fetched —
  // it's the single source of truth so the card and lightbox always match.
  // Fall back to the deterministic picker for any item that wasn't hydrated
  // through the store (e.g. one-off renders from external sources).
  const gradient = item.gradient || pickMusicGradient(item.id);
  const hasThumbnail = !!item.thumbnailPath;
  const soundRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const navigation = useNavigation();

  const stopAndUnload = useCallback(() => {
    if (soundRef.current) {
      soundRef.current.pause();
      soundRef.current.removeAttribute('src');
      soundRef.current.load();
      soundRef.current = null;
    }
    releaseActivePlayback(stopAndUnload);
    if (isMountedRef.current) {
      setIsPlaying(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const unsubscribe = navigation.addListener('blur', stopAndUnload);
    return () => {
      isMountedRef.current = false;
      stopAndUnload();
      unsubscribe();
    };
  }, [navigation, stopAndUnload]);

  const handleTogglePlay = useCallback(async (e) => {
    // Preview audio is web-only (same as the old hover behavior). On native,
    // let the press fall through so the card opens the lightbox player.
    if (!isWeb || !item.cdnPath) return;

    e?.stopPropagation?.();
    if (e?.nativeEvent) {
      e.nativeEvent.stopPropagation?.();
    }

    if (isPlaying) {
      stopAndUnload();
      return;
    }

    // The browser's play promise resolves asynchronously. Without this guard,
    // rapid clicks can launch multiple audio elements before playback begins.
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    claimActivePlayback(stopAndUnload);

    try {
      if (!soundRef.current) {
        const sound = new globalThis.Audio(item.cdnPath);
        sound.volume = 0.3;
        sound.loop = false;
        sound.addEventListener('ended', stopAndUnload);
        soundRef.current = sound;
        await sound.play();

        if (
          !isMountedRef.current ||
          activeStopPlayback !== stopAndUnload ||
          soundRef.current !== sound
        ) {
          sound.pause();
          sound.removeAttribute('src');
          sound.load();
          return;
        }
      } else {
        soundRef.current.currentTime = 0;
        await soundRef.current.play();
      }
      if (isMountedRef.current && activeStopPlayback === stopAndUnload) {
        setIsPlaying(true);
      }
    } catch {
      // Audio load can fail silently
      stopAndUnload();
    } finally {
      isLoadingRef.current = false;
    }
  }, [isPlaying, item.cdnPath, stopAndUnload]);

  const duration = item.metadata?.duration
    ? `${Math.floor(item.metadata.duration / 60)}:${String(Math.floor(item.metadata.duration % 60)).padStart(2, '0')}`
    : null;

  const waveActive = isPlaying || hovered;

  return (
    <>
      <View style={mz.clip}>
        <View style={[mz.zoom, hovered && mz.zoomHovered]}>
          {/* Always render the gradient first so the card has a visible
              background even if the optional thumbnail is missing, fails
              to load, or hasn't finished decoding yet. */}
          <LinearGradient
            colors={gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.cover, style]}
          />
          {hasThumbnail ? (
            <Image
              source={{ uri: item.thumbnailPath }}
              style={[styles.cover, style]}
              pointerEvents="none"
              contentFit="cover"
              placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
              transition={200}
              recyclingKey={item.id}
              priority="low"
            />
          ) : null}
        </View>
      </View>

      <View style={styles.centerContent} pointerEvents="box-none">
        <Pressable
          onPress={handleTogglePlay}
          // Lets the parent card ignore presses that originate in this control
          // (see ExploreCard.handlePress) so a play toggle never opens the
          // lightbox — even if event propagation isn't stopped.
          {...(isWeb ? {
            dataSet: { exploreMusicPlay: 'true' },
            onClick: (e) => {
              e.stopPropagation();
            },
          } : {})}
          accessibilityRole="button"
          accessibilityLabel={isPlaying ? 'Pause preview' : 'Play preview'}
          style={[styles.playCircle, (isPlaying || hovered) && styles.playCircleActive]}
        >
          <Icon name={isPlaying ? 'pause' : 'play'} size={18} color="#fff" solid />
        </Pressable>
        {duration && (
          <View style={styles.durationBadge} pointerEvents="none">
            <Icon name="clock" size={9} color="rgba(255,255,255,0.8)" />
            <Animated.Text style={styles.durationText}>{duration}</Animated.Text>
          </View>
        )}
      </View>

      <View style={styles.waveformContainer} pointerEvents="none">
        {[...Array(NUM_BARS)].map((_, i) => (
          <WaveformBar key={i} index={i} itemId={item.id} active={waveActive} />
        ))}
      </View>

      <ExploreCardOverlay
        prompt={item.prompt}
        modelName={item.modelName}
        modelSlug={item.modelSlug}
        mediaType="music"
        isPlusShowcase={item.flags?.isPlusShowcase}
        hovered={hovered}
        variationCount={item.variationCount}
      />
    </>
  );
});

ExploreCardMusic.displayName = 'ExploreCardMusic';

const styles = StyleSheet.create({
  cover: {
    ...StyleSheet.absoluteFillObject,
  },
  centerContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    zIndex: 2,
  },
  playCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.25)',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  playCircleActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.7)',
    borderColor: 'rgba(255,255,255,0.4)',
  },
  durationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'rgba(0,0,0,0.3)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 10,
    fontWeight: '600',
  },
  waveformContainer: {
    position: 'absolute',
    bottom: 56,
    left: 12,
    right: 12,
    height: 32,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  waveBar: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderRadius: 1.5,
    minHeight: 3,
  },
});

export default ExploreCardMusic;
